/**
 * Per-journal results, plus the line editor for a failed journal.
 *
 * Successes are immutable — a journal with an iplicit doc number is never editable
 * or re-sent. A failure expands to an editable copy (period, doc date, and every
 * line); saving PATCHes the edit, which the server re-validates, then retries just
 * that journal.
 */
import { useState } from "react";
import {
  money, saveJournalEdit, retryRun, type JournalLine, type JournalRecord, type Run,
} from "../../payroll/api";

export default function ResultsTable({
  run, canEdit, busy, onRunChange, onRetryAll,
}: {
  run: Run;
  canEdit: boolean;
  busy: boolean;
  onRunChange: (r: Run) => void;
  onRetryAll: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const failed = run.journals.filter((j) => j.status === "failed");
  const created = run.journals.filter((j) => j.status === "draft_created").length;

  return (
    <div className="results">
      <div className={`alert ${failed.length ? "alert--error" : "alert--ok"}`}>
        {failed.length
          ? `✕ ${created} of ${run.journals.length} journals created as drafts. ${failed.length} failed — the successful ones are kept. Edit the failing lines below, then retry just those.`
          : `✓ All ${run.journals.length} journals created as unposted drafts in iplicit for period ${run.periodCode}.`}
      </div>

      <div className="grid-table">
        <div className="grid-table__header result-grid">
          <div>Department</div><div>Our ref</div><div className="num">Lines</div>
          <div className="num">Value</div><div>iplicit doc no.</div><div>Status</div>
        </div>

        {run.journals.map((j) => (
          <div key={j.importDocRef}>
            <div className={`grid-table__row result-grid${j.status === "failed" ? " failed" : ""}`}>
              <div>{j.payrollName}</div>
              <div className="mono muted">MJ-{j.periodCode}-{String(j.importDocRef).padStart(2, "0")}</div>
              <div className="num">{j.lines.length}</div>
              <div className="num">{money(j.value)}</div>
              <div className="mono">{j.iplicitDocNo ?? "—"}</div>
              <div className="result-status">
                <span className={`status-tag status-tag--${j.status}`}>{label(j)}</span>
                {j.errorText && <span className="error-text">{j.errorText}</span>}
                {canEdit && j.status === "failed" && (
                  <button className="btn btn--small" onClick={() => setEditing(editing === j.importDocRef ? null : j.importDocRef)}>
                    {editing === j.importDocRef ? "Close editor" : "Edit lines & retry"}
                  </button>
                )}
              </div>
            </div>

            {editing === j.importDocRef && (
              <JournalEditor
                run={run}
                journal={j}
                onClose={() => setEditing(null)}
                onSaved={(r) => { onRunChange(r); setEditing(null); }}
              />
            )}
          </div>
        ))}
      </div>

      <div className="results__footer">
        <span className="muted">
          Journals land in iplicit as <strong>unposted drafts</strong> under batch “{run.journals[0]?.description.split(" - ")[0]}”.
          Nothing hits the ledger until you post them there.
        </span>
        {canEdit && failed.length > 0 && (
          <button className="btn btn--primary" disabled={busy} onClick={onRetryAll}>
            {busy ? "Retrying…" : `Retry all ${failed.length} failed as-is`}
          </button>
        )}
      </div>
    </div>
  );
}

function label(j: JournalRecord): string {
  if (j.status === "failed") return "Failed";
  if (j.status === "draft_created") return j.edited ? "Draft created (edited)" : "Draft created";
  if (j.status === "posting") return "Posting…";
  return "Pending";
}

/* ------------------------------------------------------------------ editor */

function JournalEditor({
  run, journal, onClose, onSaved,
}: { run: Run; journal: JournalRecord; onClose: () => void; onSaved: (r: Run) => void }) {
  const [lines, setLines] = useState<JournalLine[]>(journal.lines.map((l) => ({ ...l })));
  const [periodCode, setPeriodCode] = useState(journal.periodCode);
  const [docDate, setDocDate] = useState(journal.docDate);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const balance = round2(lines.reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const balanced = Math.abs(balance) < 0.005;

  const patch = (i: number, field: keyof JournalLine, value: string) =>
    setLines((ls) => ls.map((l, k) => (k === i ? { ...l, [field]: field === "amount" ? (value as unknown as number) : value } : l)));

  async function save(retry: boolean) {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        lines: lines.map((l) => ({
          account: String(l.account).trim(),
          department: l.department ? String(l.department).trim() : null,
          costCentre: l.costCentre ? String(l.costCentre).trim() : null,
          description: String(l.description).trim(),
          amount: Number(l.amount),
        })),
        periodCode,
        docDate,
      };
      const saved = await saveJournalEdit(run.id, journal.importDocRef, payload);
      if (!retry) { onSaved(saved.run); return; }
      const res = await retryRun(run.id);
      onSaved(res.run);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="journal-editor">
      <div className="journal-editor__head">
        <div className="error-text"><strong>iplicit rejected this journal:</strong> {journal.errorText}</div>
        <div className="journal-editor__header-fields">
          <label>Period <input className="input mono" value={periodCode} onChange={(e) => setPeriodCode(e.target.value)} /></label>
          <label>Doc date <input className="input" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></label>
        </div>
      </div>

      <div className="journal-editor__grid journal-editor__grid--head">
        <div>Account</div><div>Dept</div><div>Cost centre</div><div>Description</div><div className="num">Amount (DR +)</div><div />
      </div>

      {lines.map((l, i) => (
        <div className="journal-editor__grid" key={i}>
          <input className="input mono" value={l.account} onChange={(e) => patch(i, "account", e.target.value)} />
          <input className="input" value={l.department ?? ""} onChange={(e) => patch(i, "department", e.target.value)} />
          <input className="input" value={l.costCentre ?? ""} onChange={(e) => patch(i, "costCentre", e.target.value)} />
          <input className="input" value={l.description} onChange={(e) => patch(i, "description", e.target.value)} />
          <input className="input num" value={String(l.amount)} onChange={(e) => patch(i, "amount", e.target.value)} />
          <button className="line-remove" title="Remove line" onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}>×</button>
        </div>
      ))}

      {error && <div className="alert alert--error">{error}</div>}

      <div className="journal-editor__footer">
        <div className="journal-editor__footer-left">
          <button
            className="btn btn--dashed"
            onClick={() => setLines((ls) => [...ls, { account: "", department: "", costCentre: "C9999", description: "", amount: 0 }])}
          >
            + Add line
          </button>
          <span className={`badge ${balanced ? "badge--ok" : "badge--bad"}`}>
            {balanced ? "✓ Balances to £0.00" : `✕ Out by ${money(balance)} — fix before retrying`}
          </span>
        </div>
        <div className="journal-editor__footer-right">
          <button className="btn" onClick={() => setLines(journal.lines.map((l) => ({ ...l })))}>Reset to parsed</button>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={!balanced || saving} onClick={() => void save(true)}>
            {saving ? "Retrying…" : "Save & retry this journal"}
          </button>
        </div>
      </div>

      <p className="muted">
        Edits apply to this submission only and are audited; the parsed original is kept. To stop it
        recurring next month, correct the mapping in <strong>Admin → Departments</strong> or{" "}
        <strong>Admin → Nominals</strong>.
      </p>
    </div>
  );
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
